import React, {Fragment, useState} from 'react'
import type {FunctionComponent, SyntheticEvent} from 'react'
import {useOutletContext, useRouteLoaderData, useSubmit} from '@remix-run/react'
import clsx from 'clsx'
import {useReportItemSeen, useReportWidgetClick, useSendPartnerProductClickout} from '~/hooks/analytic-hooks'
import {getSchemaPriceValidUntil, objectToQueryString} from '~/utils/commons'
import {IconAmazonLogoSvg} from '~/icons/product-page/icon-amazon-logo-svg'
import type {Product, RootAndListContext, RootLoaderResponse, SlugApiResponse} from '~/types'
import {getTranslation} from '~/localization/translation'
import {BrgIconAppLogoLetter} from '~/icons/brg-icon-app-logo-letter'
import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'
import {getDomainConfig} from '~/domains'
import {grantAllConsent} from '~/utils/consent-manager.client'
import {ValueForMoneyTagIcon} from '~/icons/value-for-money-tag'
import {CompareIcon} from '~/icons/compare-icon'
import {cssDomains} from '~/utils/constants'
import {DiscountBadge} from '../badges/discount-badge'
import ProductCardBadge from '../badges/card-placement-badge'
import VariantsIndication from '../badges/variants-indication'
import {EditorsChoiceBadge} from '../badges/editors-choice-badge'
import {DiscountBadgeReorganized} from '../badges/discount-badge-reorganized'
import KeyFeatures from './key-features'
import DefaultCta from './default-cta'
import ListingsCta from './listings-cta'

export interface ProductCardProps {
  index: number
  product: Product
  showListingsCta: boolean
  showProductPageLinks?: boolean
}

const dealsBadgesByPlacement = {
  0: 'valueForMoney',
  1: 'greatChoice',
} as Record<number, string>

export const ProductCard: FunctionComponent<ProductCardProps> = ({
  product,
  index,
  showListingsCta,
  showProductPageLinks = true,
}) => {
  const {
    image,
    name: initialName,
    nameTranslation,
    brand = '',
    listScore,
    affiliateLink,
    discount,
    badge,
    coupon,
    isSellerProduct,
    asin,
    price,
    productPageSlug,
    forcedPosition,
    storeListings,
  } = product

  const {
    locale,
    language,
    forcedLanguage,
    featureFlags,
    trackAnalytics,
    currencyCode,
    domain,
    hostname,
    renderTimestamp,
  } = useRouteLoaderData('root') as RootLoaderResponse

  const name = (forcedLanguage ? nameTranslation : initialName) ?? initialName

  const {
    dataResponse: {productsCategory} = {} as SlugApiResponse,
    clickId,
    setShowCookieConsentGdpr,
  } = useOutletContext<RootAndListContext>()
  const translations = getTranslation(language)
  const submit = useSubmit()
  const {styles: stylesBydomain, domainType, domainName} = getDomainConfig(domain, locale)
  const [isKeyFeaturesOpen, setIsKeyFeaturesOpen] = useState(false)

  const isDiscountExist = Boolean(discount)

  const showDealSpecs = domainType === 'deals' && index === 0
  const showDealPriceDropBadge = domainType === 'deals' && !!discount && discount >= 20
  const showCompareButton = locale?.toLowerCase() === 'de' && index < 3

  const showVariantsIndication =
    featureFlags?.showVariantsIndication && product?.variationDetails && product?.variationDetails.length > 0

  const shouldForceTrackAnalytics = Boolean(
    featureFlags?.cookieConsentVariation === 'opt-in' &&
    (!trackAnalytics?.permission || trackAnalytics?.permission === 'denied'),
  )

  const trackSearchParams = objectToQueryString({
    session_id: trackAnalytics?.session_id,
    ...trackAnalytics?.pageLoadData,
    page_id: trackAnalytics?.page_id,
    user_id: trackAnalytics?.user_id,
  })

  const affiliateLinkWithClickId = affiliateLink.url.replaceAll(CLICK_ID_PLACEHOLDER, clickId)

  const redirectUrl = shouldForceTrackAnalytics
    ? `/redirect-analytics?affiliateLink=${encodeURIComponent(affiliateLinkWithClickId)}&${trackSearchParams}`
    : affiliateLinkWithClickId

  const scoreFormatter = new Intl.NumberFormat(language, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })

  const displayScore = listScore

  const displayReorganizedLayout = Boolean(featureFlags?.showEngagingElements || featureFlags?.useReorganizedCardLayout)
  const isStoreInfoExists = Boolean(storeListings && storeListings?.amazon)
  const showBuyingOptionsCta = cssDomains.includes(domainName) && isStoreInfoExists

  const reportWidgetClick = useReportWidgetClick()
  const reportClickout = useSendPartnerProductClickout({
    product,
    index: index + 1,
    locale,
    listName: 'category-products',
    categorySlug: productsCategory?.slug,
    categoryName: productsCategory?.name,
  })

  const [cardRef] = useReportItemSeen({
    item_list_id: productsCategory?.slug,
    item_name: name,
    item_source: 'list',
    partner_item_brand: brand,
    partner_item_id: asin,
    affiliation: affiliateLink.type,
    is_dws: isSellerProduct,
    price: price?.amount,
    currency: price?.currency,
    click_element_name: 'category-product',
    index: index + 1,
    variation: JSON.stringify({
      hasVariantIndication: showVariantsIndication,
      forcedPosition,
    }),
  })

  const handleCardClick = (_e: SyntheticEvent) => {
    if (shouldForceTrackAnalytics) {
      setShowCookieConsentGdpr(false)
      grantAllConsent()
      submit(
        {
          'accept-gdpr': 'granted',
          redirect: 'true',
          'track-analytics-data': JSON.stringify({...trackAnalytics, permission: 'granted'}),
        },
        {method: 'POST', action: '/cookie-consent', replace: false, navigate: false},
      )
    }

    reportClickout()
    reportWidgetClick({
      widget_name: 'product-card',
      widget_place: 'list',
      variation: JSON.stringify({
        hasVariantIndication: showVariantsIndication,
        forcedPosition,
      }),
    })

    if (typeof Storage != 'undefined') {
      localStorage.setItem('clickTime', Date.now().toString())
      if (featureFlags?.showHustonProblemPopup) localStorage.setItem('redirectUrl', redirectUrl)
    }
  }

  const reportVariantsClicked = () => {
    reportWidgetClick({
      widget_name: 'variants-indication',
      widget_place: `card-${index + 1}`,
      trigger_type: 'click',
      variation: 'product-card',
    })
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Product',
            name: name,
            image: [image],
            description: name,
            brand: {
              '@type': 'Brand',
              name: brand,
            },
            offers: {
              '@type': 'Offer',
              url: `https://${hostname}${affiliateLinkWithClickId}`,
              priceCurrency: currencyCode,
              price: price?.amount,
              priceValidUntil: getSchemaPriceValidUntil(renderTimestamp, 'en-CA'),
              itemCondition: 'https://schema.org/NewCondition',
              availability: 'https://schema.org/InStock',
              seller: {
                '@type': 'Organization',
                name: brand ?? 'Amazon',
              },
            },
          }),
        }}
      ></script>

      <div
        data-testid={`product-card-${index}`}
        className="border-gray relative z-20 flex h-full min-h-52 w-full flex-col rounded-3xl border bg-white shadow transition-all duration-200 ease-in-out"
      >
        <div className="relative">
          <ProductCardBadge
            index={index}
            badge={domainType === 'deals' ? dealsBadgesByPlacement[index] : badge}
            coupon={coupon}
            className="absolute left-6 top-0 overflow-hidden rounded-b-lg rounded-t-none"
          />

          {isDiscountExist && featureFlags?.useReorganizedCardLayout ? (
            <DiscountBadgeReorganized
              className={clsx(
                'absolute right-0 top-[18px] hidden rounded-l-2xl px-3 py-0.5 tablet:block',
                stylesBydomain.LISTPAGE_BADGE_CARD_DISCOUNT_BG,
                stylesBydomain.LISTPAGE_BADGE_CARD_DISCOUNT_TEXT,
                'listpage_badge_card_discount_bg listpage_badge_card_discount_text',
              )}
              discount={discount}
            />
          ) : null}

          <div
            className={clsx(
              'flex w-full flex-col tablet:px-5 tablet:pb-5 tablet:pt-5',
              displayReorganizedLayout ? 'tablet:pb-1' : 'tablet:pb-5',
            )}
          >
            <div
              className={clsx(
                'flex min-h-52 w-full flex-row tablet:flex-col',
                displayReorganizedLayout ? 'tablet:gap-0' : 'tablet:gap-4',
              )}
            >
              <div
                className={clsx(
                  'relative flex min-w-44 items-center justify-center tablet:justify-between tablet:px-10',
                  featureFlags?.useReorganizedCardLayout && isDiscountExist ? 'tablet:mt-6' : 'tablet:mt-3',
                )}
                ref={cardRef}
              >
                <div className="hidden flex-col tablet:flex">
                  {/* ------------------------------------------------ Score mobile ------------------------------------------------ */}
                  <div className="relative flex flex-row tablet:flex-col tablet:gap-1">
                    <a
                      className="absolute inset-0"
                      href={redirectUrl}
                      onClick={handleCardClick}
                      aria-label={`view ${brand}`}
                      target="_blank"
                      rel="sponsored noreferrer"
                    ></a>
                    <label
                      className={clsx(
                        'flex justify-center text-3xl',
                        featureFlags?.useReorganizedCardLayout ? 'mx-auto' : 'mr-2',
                        stylesBydomain.LISTPAGE_BADGE_SCORE_TEXT,
                        'listpage_badge_score_text',
                      )}
                    >
                      {scoreFormatter.format(displayScore)}
                    </label>
                    <div className="flex items-center text-xs leading-tight">
                      {featureFlags?.useReorganizedCardLayout ? null : (
                        <BrgIconAppLogoLetter className="mr-1 h-[13px] w-[11px]" />
                      )}
                      <label className={clsx(featureFlags?.useReorganizedCardLayout ? 'mx-auto' : '')}>
                        {translations.ProductCard.scoreSuffix}
                      </label>
                    </div>
                  </div>
                </div>

                {/* ------------------------------------------------ Image ------------------------------------------------ */}
                <div className="relative flex flex-col items-center justify-center">
                  <a
                    href={redirectUrl}
                    onClick={handleCardClick}
                    aria-label={`view ${brand}`}
                    target="_blank"
                    rel="sponsored noreferrer"
                  >
                    <img
                      src={image}
                      loading={index > 2 ? 'lazy' : 'eager'}
                      alt={name?.slice(0, 10)}
                      className="h-[120px] w-[120px] shrink-0 grow-0 object-scale-down mix-blend-multiply"
                    />
                  </a>
                </div>
              </div>

              <div
                className={clsx(
                  'relative flex flex-col gap-1 overflow-hidden desktop:py-10 desktop:pr-4 tablet:gap-2',
                  displayReorganizedLayout ? 'tablet:flex-col' : 'tablet:flex-col-reverse',
                )}
              >
                <div className="flex h-full w-full items-center desktop:flex-wrap tablet:justify-between tablet:gap-y-2">
                  <div className="flex flex-row items-center">
                    {/* ------------------------------------------------------ Brand ------------------------------------------------ */}

                    <a
                      href={redirectUrl}
                      onClick={handleCardClick}
                      aria-label={`view ${brand}`}
                      target="_blank"
                      rel="sponsored noreferrer"
                    >
                      <p className="mr-2 line-clamp-1 text-base font-bold capitalize" data-testid="category-card-brand">
                        {brand}
                      </p>
                    </a>
                    {/* ------------------------------------------------------ Discount ------------------------------------------------ */}
                    {isDiscountExist && (
                      <DiscountBadge
                        discount={discount}
                        className={clsx(
                          'mr-2 h-auto max-w-fit rounded-2xl px-2 py-1 text-sm desktop:block',
                          featureFlags?.useReorganizedCardLayout && 'hidden',
                          stylesBydomain.LISTPAGE_BADGE_CARD_DISCOUNT_OLD_BG,
                          stylesBydomain.LISTPAGE_BADGE_CARD_DISCOUNT_OLD_TEXT,
                          'listpage_badge_card_discount_old_bg listpage_badge_card_discount_old_text',
                        )}
                      />
                    )}
                  </div>
                  {/* ------------------------------------------------------ Editors Choice Badge ------------------------------------------------ */}
                  {isSellerProduct && (
                    <EditorsChoiceBadge className="my-0 h-fit shrink-0 items-center tablet:my-1 mobile:my-0" />
                  )}
                </div>

                {/* ------------------------------------------------------ Name ------------------------------------------------ */}
                <a
                  href={redirectUrl}
                  onClick={handleCardClick}
                  aria-label={`view ${brand}`}
                  target="_blank"
                  rel="sponsored noreferrer"
                >
                  <h2 className="line-clamp-3 overflow-hidden text-lg font-normal leading-7 no-underline tablet:mt-1 tablet:line-clamp-2 tablet:leading-7">
                    {name}
                  </h2>
                </a>

                {/* ------------------------------------------------ Variants Desktop------------------------------------------------ */}
                {showVariantsIndication && (
                  <a
                    href={redirectUrl}
                    onClick={handleCardClick}
                    aria-label={`view ${brand}`}
                    target="_blank"
                    rel="sponsored noreferrer"
                    className="mt-5 flex flex-row items-center gap-1 tablet:hidden"
                  >
                    <VariantsIndication
                      className="flex flex-row gap-1 truncate"
                      index={index + 1}
                      variations={product?.variationDetails}
                      reportVariantsClicked={reportVariantsClicked}
                    />
                  </a>
                )}

                {/* ------------------------------------------------  Deal Specs DESKTOP (Deals domain type)------------------------------------------------ */}
                {showDealSpecs && (
                  <div className="mx-auto mt-4 flex w-full flex-col gap-2 rounded-lg bg-green-50 p-4 shadow-md tablet:hidden">
                    {product?.dealsSpecs?.map((spec, index) => (
                      <Fragment key={`${spec}-${index}`}>
                        <div className="flex flex-row items-start gap-2 text-sm font-normal text-[#030303]">
                          <ValueForMoneyTagIcon className="shrink-0 [&>path]:fill-[#00B362]" />
                          {spec}
                        </div>
                      </Fragment>
                    ))}
                  </div>
                )}
              </div>

              <div
                className={clsx(
                  'ml-auto flex flex-col items-center justify-center gap-2 border-0 border-l border-solid desktop:py-2 tablet:ml-0 tablet:min-w-full tablet:border-none',
                  showListingsCta ? 'w-64 min-w-64 desktop:px-2' : 'w-56 min-w-56 desktop:px-4',
                )}
              >
                <div className="flex flex-col tablet:hidden">
                  {/* ------------------------------------------------ Score desktop ------------------------------------------------ */}
                  <div className="relative flex flex-row gap-2">
                    <a
                      className="absolute inset-0"
                      href={redirectUrl}
                      onClick={handleCardClick}
                      aria-label={`view ${brand}`}
                      target="_blank"
                      rel="sponsored noreferrer"
                    ></a>
                    <label
                      className={clsx(
                        'flex justify-center text-3xl',
                        stylesBydomain.LISTPAGE_BADGE_SCORE_TEXT,
                        'listpage_badge_score_text',
                      )}
                    >
                      {scoreFormatter.format(displayScore)}
                    </label>
                    <div className="flex items-center text-xs leading-tight">
                      {featureFlags?.useReorganizedCardLayout ? null : (
                        <BrgIconAppLogoLetter className="mr-1 h-[13px] w-[11px]" />
                      )}
                      <label>{translations.ProductCard.scoreSuffix}</label>
                    </div>
                  </div>
                </div>

                {!showBuyingOptionsCta && displayReorganizedLayout ? (
                  <a
                    href={redirectUrl}
                    onClick={handleCardClick}
                    aria-label={`view ${brand}`}
                    target="_blank"
                    rel="sponsored noreferrer"
                  >
                    <IconAmazonLogoSvg
                      className="mb-1 mt-1 h-5 object-scale-down tablet:hidden"
                      loading={index > 2 ? 'lazy' : 'eager'}
                    />
                  </a>
                ) : null}

                {/* ------------------------------------------------ CTA ------------------------------------------------ */}
                {showBuyingOptionsCta ? (
                  <ListingsCta
                    product={product}
                    index={index + 1}
                    className="flex flex-row gap-1.5 p-0.5"
                    placement="category-product-card"
                    showProductPageLinks={showProductPageLinks}
                  />
                ) : (
                  <DefaultCta
                    product={product}
                    showDealSpecs={showDealSpecs}
                    redirectUrl={redirectUrl}
                    showPrimeDayLogo={!!discount}
                    showVariantsIndication={showVariantsIndication}
                    reportVariantsClicked={reportVariantsClicked}
                    handleCardClick={handleCardClick}
                    showDealPriceDropBadge={showDealPriceDropBadge}
                    index={index}
                    className={clsx(
                      'flex w-full',
                      displayReorganizedLayout || featureFlags?.enableChristmasBadge ? 'flex-col-reverse' : 'flex-col',
                    )}
                  />
                )}

                {/* -------------------------------------- Compare Button -------------------------------------- */}
                {showCompareButton && (
                  <a
                    href="#compare-top-picks"
                    onClick={() => {
                      reportWidgetClick({
                        widget_name: 'compare-button',
                        widget_place: `card-${index + 1}`,
                      })
                    }}
                    className={clsx(
                      'mx-auto mt-2 flex flex-row items-center gap-3 uppercase',
                      stylesBydomain.LISTPAGE_BUTTON_LINK,
                      'listpage_button_link',
                    )}
                  >
                    <CompareIcon
                      className={clsx(stylesBydomain.LISTPAGE_BUTTON_ICON_FILL, 'listpage_button_icon_fill')}
                    />
                    {translations.ProductCard.compare}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* ------------------------------------------------------Key features------------------------------------------------ */}

        <KeyFeatures
          placement={index + 1}
          productPageSlug={productPageSlug ?? ''}
          showProductPageLinks={showProductPageLinks}
          handleCardClick={handleCardClick}
          product={product}
          affiliateLinkWithClickId={affiliateLinkWithClickId}
          isKeyFeaturesOpen={isKeyFeaturesOpen}
          setIsKeyFeaturesOpen={setIsKeyFeaturesOpen}
        />
      </div>
    </>
  )
}
